import { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { Tooltip, OverlayTrigger } from "react-bootstrap";

interface CalendarProvider {
  calendar_provider_id: number;
  calendar_provider_key: string;
  calendar_provider_display_name: string;
}

interface CalendarAuth {
  calendar_auth_id: number;
  external_auth_user_id: string;
  external_auth_user_email: string;
  external_auth_user_name: string;
  access_token: string;
  refresh_token: string;
  token_expiration_datetime: string;
  sync_token: string | null;
  sync_token_expiration_datetime: string | null;
  created_datetime: string;
  updated_datetime: string;
  is_authorized: boolean;
  user_id: number;
  tenant_id: number;
  calendar_provider: CalendarProvider;
  updated_by: number;
}

interface LinkedCalendar {
  calendar_auth_linked_calendar_id: number;
  calendar_id: string;
  name: string;
  description: string;
  timezone: string;
  is_primary: boolean;
  last_synced_at: string;
  raw_data: string;
  created_datetime: string;
  updated_datetime: string;
  calendar_auth_id: number;
  tenant_id: number;
  calendar_color_category_id: number;
  created_by: number;
  updated_by: number;
}

interface ParsedRawData {
  backgroundColor?: string;
  foregroundColor?: string;
  accessRole?: string;
  colorId?: string;
}

const CalendarSettings = () => {
  const { auth, selectedTenantId } = useContext(AuthContext);
  const MySwal = withReactContent(Swal);
  const [loading, setLoading] = useState(false);
  const [calendarAuths, setCalendarAuths] = useState<CalendarAuth[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [showAddCalendarModal, setShowAddCalendarModal] = useState(false);
  const [selectedAuth, setSelectedAuth] = useState<CalendarAuth | null>(null);
  const [linkedCalendars, setLinkedCalendars] = useState<LinkedCalendar[]>([]);
  const [linkedCalendarsLoading, setLinkedCalendarsLoading] = useState(false);

  const fetchCalendarAuths = async () => {
    const user_id = auth?.user_id;
    if (!user_id || !selectedTenantId) {
      setAccountsLoading(false);
      return;
    }

    setAccountsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/calendar-auth/?tenant_id=${selectedTenantId}&user_id=${user_id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCalendarAuths(data.results || []);
      }
    } catch (error) {
      console.error("Error fetching calendar auths:", error);
    } finally {
      setAccountsLoading(false);
    }
  };

  const fetchLinkedCalendars = async (calendarAuthId: number) => {
    if (!selectedTenantId) return;

    setLinkedCalendarsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/calendar-auth-linked-calendar/?tenant_id=${selectedTenantId}&calendar_auth_id=${calendarAuthId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setLinkedCalendars(data.results || []);
      }
    } catch (error) {
      console.error("Error fetching linked calendars:", error);
    } finally {
      setLinkedCalendarsLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarAuths();
  }, [selectedTenantId, auth?.user_id]);

  const handleSelectAuth = (account: CalendarAuth) => {
    setSelectedAuth(account);
    fetchLinkedCalendars(account.calendar_auth_id);
  };

  const handleConnectGoogleCalendar = async () => {
    const user_id = auth?.user_id;
    if (!user_id || !selectedTenantId) {
      MySwal.fire({
        title: "Error",
        text: "Authentication details missing. Please login again.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    setShowAddCalendarModal(false);
    setLoading(true);
    localStorage.setItem("last_auth_provider", "GoogleCalendar");
    try {
      const response = await fetch(
        `${API_BASE_URL}/google-calendar/auth/?user_id=${user_id}&tenant_id=${selectedTenantId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.auth_url) {
          window.location.href = data.auth_url;
        } else {
          MySwal.fire({
            title: "Error!",
            text: "Failed to get authorization URL.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      } else {
        MySwal.fire({
          title: "Error!",
          text: "Failed to initiate Google Calendar connection.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      MySwal.fire({
        title: "Error!",
        text: "An error occurred while connecting to Google Calendar.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectCalendar = async (account: CalendarAuth) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to delete integration with "${account.external_auth_user_email || "this calendar"}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Delete!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setAccountsLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/calendar-auth/${account.calendar_auth_id}/`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${localStorage.getItem("authCred")}`,
            },
          }
        );

        if (response.ok) {
          MySwal.fire({
            title: "Disconnected!",
            text: "Calendar disconnected successfully!",
            icon: "success",
            confirmButtonText: "OK",
          });
          setSelectedAuth(null);
          setLinkedCalendars([]);
          await fetchCalendarAuths();
        } else {
          MySwal.fire({
            title: "Error!",
            text: "Failed to disconnect calendar.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      } catch (error) {
        MySwal.fire({
          title: "Error!",
          text: "An error occurred while disconnecting calendar.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        setAccountsLoading(false);
      }
    }
  };

  const getProviderIcon = (providerKey: string) => {
    switch (providerKey?.toUpperCase()) {
      case "GOOGLE":
      case "GOOGLE_CALENDAR":
        return "ti ti-brand-google";
      case "OUTLOOK":
      case "MICROSOFT":
        return "ti ti-brand-windows";
      default:
        return "ti ti-calendar-event";
    }
  };

  const getProviderColor = (providerKey: string) => {
    switch (providerKey?.toUpperCase()) {
      case "GOOGLE":
      case "GOOGLE_CALENDAR":
        return "#4285F4";
      case "OUTLOOK":
      case "MICROSOFT":
        return "#0078D4";
      default:
        return "#6366f1";
    }
  };

  const getProviderBgGradient = (providerKey: string) => {
    switch (providerKey?.toUpperCase()) {
      case "GOOGLE":
      case "GOOGLE_CALENDAR":
        return "linear-gradient(135deg, #e8f0fe 0%, #fce8e6 100%)";
      case "OUTLOOK":
      case "MICROSOFT":
        return "linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%)";
      default:
        return "linear-gradient(135deg, #ede9fe 0%, #e0e7ff 100%)";
    }
  };

  const parseRawData = (rawDataStr: string): ParsedRawData => {
    try {
      return JSON.parse(rawDataStr);
    } catch {
      return {};
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="calendar-settings-container">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="fs-20">Calendar Settings</h4>
      </div>

      <div className="tab-content">
        <div className="tab-pane fade show active">
          <>
            <div className="d-flex justify-content-between mb-3 align-items-center">
              {!selectedAuth && (
                <div className="ms-auto">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowAddCalendarModal(true)}
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    ) : (
                      <i className="fa fa-plus me-1"></i>
                    )}
                    Add Calendar
                  </button>
                </div>
              )}
            </div>

            {accountsLoading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "200px" }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : selectedAuth ? (
              <div className="calendar-detail-view px-2">
                <div className="d-flex align-items-center mb-4">
                  <button
                    type="button"
                    className="btn me-3 border-0 bg-transparent text-primary"
                    onClick={() => {
                      setSelectedAuth(null);
                      setLinkedCalendars([]);
                    }}
                    style={{ fontSize: "1.2rem" }}
                  >
                    <i className="fa fa-arrow-left"></i>
                  </button>
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-circle me-3"
                    style={{
                      width: "40px",
                      height: "40px",
                      background: getProviderBgGradient(selectedAuth.calendar_provider?.calendar_provider_key),
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className={getProviderIcon(selectedAuth.calendar_provider?.calendar_provider_key)}
                      style={{
                        fontSize: "1.2rem",
                        color: getProviderColor(selectedAuth.calendar_provider?.calendar_provider_key),
                      }}
                    ></i>
                  </div>
                  <div>
                    <h5 className="mb-0 fw-bold">{selectedAuth.external_auth_user_name}</h5>
                    <small className="text-muted">{selectedAuth.external_auth_user_email}</small>
                  </div>
                  <div className="ms-auto d-flex align-items-center gap-2">
                    <button
                      className="btn btn-outline-danger btn-sm rounded-pill px-3"
                      onClick={() => handleDisconnectCalendar(selectedAuth)}
                      disabled={accountsLoading}
                    >
                      <i className="fas fa-trash me-1"></i> Delete Integration
                    </button>
                  </div>
                </div>

                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "12px" }}>
                  <div className="card-header bg-transparent border-bottom pt-4 px-4 pb-3">
                    <h5 className="card-title mb-0 fw-bold text-dark">
                      <i className="ti ti-calendar me-2" style={{ color: "#6366f1" }}></i>
                      Linked Calendars
                    </h5>
                  </div>
                  <div className="card-body px-4 pb-4">
                    {linkedCalendarsLoading ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "120px" }}>
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : linkedCalendars.length === 0 ? (
                      <div className="text-center py-4">
                        <i className="ti ti-calendar-off" style={{ fontSize: "36px", color: "#ccc" }}></i>
                        <p className="text-muted mt-2 mb-0">No linked calendars found.</p>
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-3">
                        {linkedCalendars.map((cal) => {
                          const rawData = parseRawData(cal.raw_data);
                          const bgColor = rawData.backgroundColor || "#f0f0f0";
                          const fgColor = rawData.foregroundColor || "#333333";

                          return (
                            <div
                              key={cal.calendar_auth_linked_calendar_id}
                              className="d-flex align-items-center p-3 rounded-3"
                              style={{
                                border: `1px solid ${bgColor}44`,
                                backgroundColor: `${bgColor}15`,
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = `${bgColor}30`;
                                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = `${bgColor}15`;
                                e.currentTarget.style.boxShadow = "none";
                              }}
                            >
                              <div
                                className="rounded-circle me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{
                                  width: "38px",
                                  height: "38px",
                                  backgroundColor: bgColor,
                                }}
                              >
                                <i
                                  className="ti ti-calendar-event"
                                  style={{ fontSize: "1rem", color: fgColor }}
                                ></i>
                              </div>

                              <div className="flex-grow-1 me-3" style={{ minWidth: 0 }}>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <h6
                                    className="mb-0 fw-semibold text-truncate"
                                    style={{ fontSize: "0.9rem" }}
                                  >
                                    {cal.name}
                                  </h6>
                                  {cal.is_primary && (
                                    <span
                                      className="badge rounded-pill px-2 py-1"
                                      style={{
                                        backgroundColor: "rgba(99, 102, 241, 0.12)",
                                        color: "#6366f1",
                                        fontSize: "0.65rem",
                                        fontWeight: 600,
                                        flexShrink: 0,
                                      }}
                                    >
                                      Primary
                                    </span>
                                  )}
                                </div>
                                {cal.description && (
                                  <p
                                    className="text-muted mb-0 text-truncate"
                                    style={{ fontSize: "0.78rem" }}
                                  >
                                    {cal.description}
                                  </p>
                                )}
                              </div>

                              <div className="d-flex align-items-center gap-3 flex-shrink-0">
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id={`tz-tooltip-${cal.calendar_auth_linked_calendar_id}`}>
                                      Timezone
                                    </Tooltip>
                                  }
                                >
                                  <span
                                    className="d-flex align-items-center gap-1"
                                    style={{ fontSize: "0.78rem", color: "#64748b" }}
                                  >
                                    <i className="ti ti-world" style={{ fontSize: "0.9rem" }}></i>
                                    {cal.timezone}
                                  </span>
                                </OverlayTrigger>

                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id={`sync-tooltip-${cal.calendar_auth_linked_calendar_id}`}>
                                      Last synced
                                    </Tooltip>
                                  }
                                >
                                  <span
                                    className="d-flex align-items-center gap-1"
                                    style={{ fontSize: "0.75rem", color: "#94a3b8" }}
                                  >
                                    <i className="ti ti-refresh" style={{ fontSize: "0.85rem" }}></i>
                                    {formatDateTime(cal.last_synced_at)}
                                  </span>
                                </OverlayTrigger>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : calendarAuths.length === 0 ? (
              <div className="text-center py-5">
                <i className="ti ti-calendar-off" style={{ fontSize: "48px", color: "#ccc" }}></i>
                <p className="text-muted mt-3">No calendars connected yet.</p>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => setShowAddCalendarModal(true)}
                >
                  Connect Your First Calendar
                </button>
              </div>
            ) : (
              <div className="row">
                {calendarAuths.map((account) => (
                  <div key={account.calendar_auth_id} className="col-md-4 mb-3">
                    <div
                      className="card border-0 position-relative"
                      style={{
                        borderRadius: "12px",
                        boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                        transition: "all 0.3s ease",
                        overflow: "hidden",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-5px)";
                        e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.08)";
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          padding: "2px 8px",
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          borderBottomRightRadius: "8px",
                          backgroundColor: account.calendar_provider?.calendar_provider_key === "GOOGLE" ? "rgba(34, 197, 94, 0.15)" : "rgba(239, 68, 68, 0.15)",
                          color: account.calendar_provider?.calendar_provider_key === "GOOGLE" ? "#16a34a" : "#ef4444",
                          zIndex: 1,
                        }}
                      >
                        {account.calendar_provider?.calendar_provider_key === "GOOGLE" ? "Google Calendar" : "Outlook Calendar"}
                      </div>
                      <div
                        className="card-body py-3 px-3"
                        onClick={() => handleSelectAuth(account)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="text-center pt-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-cal-${account.calendar_auth_id}`}>
                                {account.calendar_provider?.calendar_provider_display_name || "Calendar"}
                              </Tooltip>
                            }
                          >
                            <div
                              className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2"
                              style={{
                                width: "50px",
                                height: "50px",
                                background: getProviderBgGradient(account.calendar_provider?.calendar_provider_key),
                              }}
                            >
                              <i
                                className={getProviderIcon(account.calendar_provider?.calendar_provider_key)}
                                style={{
                                  fontSize: "1.5rem",
                                  color: getProviderColor(account.calendar_provider?.calendar_provider_key),
                                }}
                              ></i>
                            </div>
                          </OverlayTrigger>
                          <h6
                            className="fw-semibold mb-1"
                            style={{
                              fontSize: "0.9rem",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {account.external_auth_user_name || "Calendar"}
                          </h6>
                          <small
                            className="text-muted d-block text-truncate"
                            style={{ fontSize: "0.75rem", minHeight: "18px" }}
                          >
                            {account.external_auth_user_email}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showAddCalendarModal && (
              <div
                className="modal fade show d-block"
                tabIndex={-1}
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                onClick={(e) => {
                  if (e.target === e.currentTarget) setShowAddCalendarModal(false);
                }}
              >
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header border-0 pb-0">
                      <h5 className="modal-title">Select Calendar Provider</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowAddCalendarModal(false)}
                      ></button>
                    </div>
                    <div className="modal-body text-center pt-0 pb-5 px-4">
                      <div className="mb-4 mt-3">

                      </div>
                      <div className="d-flex flex-column gap-3">
                        <button
                          className="btn btn-outline-primary py-3 d-flex align-items-center justify-content-center w-100"
                          onClick={handleConnectGoogleCalendar}
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          ) : (
                            <i className="ti ti-brand-google text-danger me-2" style={{ fontSize: "1.2rem" }}></i>
                          )}
                          <span className="fw-medium fs-15">Google Calendar</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        </div>
      </div>
    </div>
  );
};

export default CalendarSettings;
